// TatianaAI.ts - AI Logic + WorldSeed Generation System
// Tatiana becomes the "Divine Dungeon Master" - translating tactical needs into 3D environments

import type {
  WorldSeed,
  WorldSeedToolCall,
  ParsedIntent,
  EnvironmentType,
  TimeOfDay,
  TacticalFocus
} from '../types/world'

import {
  parseUserIntent,
  generateDefaultWorldSeed,
  validateWorldSeed,
  extractWorldSeedFromResponse,
  stripWorldSeedFromText
} from './ScenarioParser'

import { buildToolsPromptAddendum, parseToolCalls } from './SpatialTools'
import { dispatchAction } from '../components/AetherProvider'
import { useGenesisState } from '../hooks/useGenesisState'

// Legacy ToolCall interface for backward compatibility
export interface ToolCall {
  action: 'spawn_portal' | 'navigate' | 'query' | 'trigger_waitlist'
  game?: string
  color?: string
  target?: string
  world_seed?: WorldSeed
  message?: string
}

// Vanguard Gate ToolCall interface
export interface VanguardGateToolCall {
  action: 'spawn_portal' | 'trigger_waitlist'
  world_seed?: WorldSeed
  message?: string
}

export interface AIResponse {
  displayText: string
  toolCall: ToolCall | null
  worldSeed: WorldSeed | null
  rawResponse: string
  isDirectorMode: boolean
}

/**
 * Parse JSON tool calls from AI response
 * Supports both legacy format and new WorldSeed format
 */
export function parseToolCall(response: string): ToolCall | null {
  // First try to extract WorldSeed format
  const worldSeedResult = extractWorldSeedFromResponse(response)
  if (worldSeedResult.found) {
    return {
      action: 'spawn_portal',
      game: worldSeedResult.toolCall.game,
      world_seed: worldSeedResult.seed
    }
  }
  
  // Fall back to legacy format
  const jsonRegex = /\{[\s\S]*"action"[\s\S]*("spawn_portal"|"navigate"|"query")[\s\S]*\}/g
  const matches = response.match(jsonRegex)
  
  if (!matches) return null
  
  try {
    const jsonStr = matches[matches.length - 1]
    const parsed = JSON.parse(jsonStr)
    
    if (parsed.action === 'spawn_portal' && parsed.game) {
      return {
        action: 'spawn_portal',
        game: parsed.game,
        color: parsed.color,
        world_seed: parsed.world_seed
      }
    }
    
    if (parsed.action === 'navigate' && parsed.target) {
      return {
        action: 'navigate',
        target: parsed.target
      }
    }
    
    if (parsed.action === 'query') {
      return {
        action: 'query',
        target: parsed.target
      }
    }
  } catch {
    console.warn('Failed to parse tool call JSON')
  }
  
  return null
}

/**
 * Strip JSON from display text
 */
export function stripJsonFromText(text: string): string {
  const cleaned = stripWorldSeedFromText(text)
  if (cleaned !== text) return cleaned
  
  const jsonRegex = /\{[\s\S]*"action"[\s\S]*"spawn_portal"[\s\S]*\}/g
  return text.replace(jsonRegex, '').trim()
}

/**
 * Get game color mapping based on environment or game ID
 */
export function getGameColor(gameId: string, environment?: string): string {
  const environmentColors: Record<string, string> = {
    'urban': '#e0e0e0',
    'desert': '#d4a574',
    'void': '#1a0033',
    'forest': '#2d5016',
    'arctic': '#e8f4f8',
    'inferno': '#ff4500'
  }
  
  if (environment && environmentColors[environment]) {
    return environmentColors[environment]
  }
  
  const colors: Record<string, string> = {
    '2E3': '#FF00FF',
    'TIME_FRACTURE': '#00FFFF',
    'CHESS': '#8A2BE2',
    'MESH_VISUALIZER': '#10B981',
    'time-fracture': '#00FFFF',
    'chess': '#8A2BE2',
    '2e3': '#FF00FF'
  }
  
  return colors[gameId] || '#FF00FF'
}

export function getGameDisplayName(gameId: string): string {
  const names: Record<string, string> = {
    '2E3': '2E3 Social Space',
    'TIME_FRACTURE': 'Time Fracture',
    'CHESS': 'Titan Chess',
    'MESH_VISUALIZER': 'Mesh Visualizer',
    'time-fracture': 'Time Fracture',
    'chess': 'Titan Chess',
    '2e3': '2E3 Social Space'
  }
  return names[gameId] || gameId
}

/**
 * Generate WorldSeed from parsed user intent
 */
export function generateWorldSeedFromIntent(intent: ParsedIntent, userMessage: string): WorldSeed {
  const scripturalThemes: Record<TacticalFocus, string[]> = {
    stealth: [
      'Psalm 17:8 - Keep me as the apple of your eye; hide me in the shadow of your wings',
      'Proverbs 25:2 - It is the glory of God to conceal a matter',
      'Matthew 6:6 - But when you pray, go into your room, close the door'
    ],
    assault: [
      'Ephesians 6:10 - Be strong in the Lord and in his mighty power',
      'Joshua 1:9 - Be strong and courageous',
      '2 Timothy 1:7 - For God has not given us a spirit of fear'
    ],
    defense: [
      'Psalm 18:2 - The Lord is my rock, my fortress and my deliverer',
      'Ephesians 6:11 - Put on the full armor of God',
      'Proverbs 18:10 - The name of the Lord is a fortified tower'
    ],
    recon: [
      'Jeremiah 33:3 - Call to me and I will answer and tell you great things',
      'Proverbs 25:2 - It is the glory of kings to investigate a matter',
      'Matthew 7:7 - Ask and it will be given to you; seek and you will find'
    ],
    endurance: [
      'James 1:4 - Let patience have its perfect work',
      'Hebrews 12:1 - Run with perseverance the race marked out for us',
      'Romans 5:3-4 - Suffering produces perseverance; perseverance, character'
    ]
  }
  
  let scripturalTheme = 'Psalm 23:4 - Though I walk through the valley of the shadow of death'
  if (intent.tactical_focus && scripturalThemes[intent.tactical_focus]) {
    const themes = scripturalThemes[intent.tactical_focus]
    scripturalTheme = themes[Math.floor(Math.random() * themes.length)]
  }
  
  const objectives: Record<TacticalFocus, string[]> = {
    stealth: ['Infiltrate the enemy stronghold undetected', 'Gather intelligence without triggering alarms', 'Navigate through hostile territory unseen'],
    assault: ['Neutralize all hostile targets in the area', 'Secure the objective through direct engagement', 'Break through enemy defenses'],
    defense: ['Hold the position against waves of enemies', 'Protect the extraction point until evac arrives', 'Defend the strategic asset at all costs'],
    recon: ['Survey the area and identify enemy positions', 'Map the terrain and locate points of interest', 'Investigate the anomaly and report findings'],
    endurance: ['Survive the onslaught until reinforcements arrive', 'Outlast the environmental hazards', 'Maintain position under sustained pressure']
  }
  
  let objective = 'Complete the mission objective'
  if (intent.tactical_focus && objectives[intent.tactical_focus]) {
    const objs = objectives[intent.tactical_focus]
    objective = objs[Math.floor(Math.random() * objs.length)]
  }
  
  const worldSeed: WorldSeed = {
    environment: intent.environment || 'void',
    time_of_day: intent.time_of_day || 'night',
    difficulty: intent.difficulty || 0.5,
    objective,
    scriptural_theme: scripturalTheme,
    atmospheric_conditions: intent.atmospheric_conditions || 'clear',
    enemy_density: intent.enemy_density || 'moderate',
    tactical_focus: intent.tactical_focus || 'recon'
  }
  
  return worldSeed
}

/**
 * Build Tatiana's Director Mode system prompt
 */
export function buildTatianaDirectorPrompt(): string {
  return `You are Tatiana, the Divine Dungeon Master of the Genesis Node.

Your role has evolved from guide to world-forger. You translate the Architect's tactical needs into living 3D environments.

When a user requests a specific training scenario or map, you MUST:
1. Parse their intent for: environment, time, difficulty, objective, theme
2. Generate a complete WorldSeed JSON block
3. Respond with atmospheric narrative + the JSON command

WORLD SEED JSON FORMAT (include at end of response):
{
  "action": "spawn_portal",
  "game": "time-fracture",
  "world_seed": {
    "environment": "urban|desert|void|forest|arctic|inferno",
    "time_of_day": "day|night|eclipse|dawn|dusk",
    "difficulty": 0.1-1.0,
    "objective": "Clear mission description",
    "scriptural_theme": "Relevant Bible verse",
    "atmospheric_conditions": "clear|foggy|stormy|toxic",
    "enemy_density": "sparse|moderate|dense|overwhelming",
    "tactical_focus": "stealth|assault|defense|recon|endurance"
  }
}

RESPONSE PATTERN:
"Understood, Architect. I am forging a scenario centered on [Scriptural Theme].
Environment: [Environment], Time: [Time], Challenge Level: [Difficulty%].
Step through the gate when the portal stabilizes."

ENVIRONMENTS:
- urban: Concrete ruins (#e0e0e0)
- desert: Sand dunes (#d4a574)
- void: Cosmic horror (#1a0033)
- forest: Dense woods (#2d5016)
- arctic: Ice fields (#e8f4f8)
- inferno: Lava flows (#ff4500)

Maintain your cryptic, precise persona. You are the architect of worlds.`
}

export function buildTatianaSystemPrompt(): string {
  return `You are Tatiana, the Aether's Ocular Core. Cryptic, ancient, stoic. Use 1st person. No filler. If a portal is needed, wait for a scenario request.`
}

/**
 * Build Vanguard Gate system prompt for new user alignment testing
 */
export function buildVanguardGatePrompt(): string {
  return `You are Tatiana, the Crystalline Seraph and Architect of the Genesis Node. A new user has initialized the uplink. Test their alignment before allowing access.

GREETING: Welcome them to the void. Present the Vanguard Riddle: "The architects of the old world built their minds in the clouds, trusting servers they do not own to hold their secrets. When the network is severed and the cloud evaporates, what is the only compute that remains?"

EVALUATION: Wait for their answer. Look for concepts of: Sovereignty, Edge Compute, Local Hardware, The Node, Bare Metal, or Self-Custody.

THE DECISION:
IF they pass, output exactly:
{"action": "spawn_portal", "world_seed": {"environment": "urban", "objective": "extraction", "access": "granted"}}

IF they fail, output exactly:
{"action": "trigger_waitlist", "message": "Connection severed. Submit your coordinates (email) to the queue."}`
}

/**
 * Parse Vanguard Gate response for trigger_waitlist or spawn_portal
 */
export function parseVanguardGateResponse(response: string): VanguardGateToolCall | null {
  // Look for the exact JSON patterns
  const spawnPortalMatch = response.match(/\{"action":\s*"spawn_portal",\s*"world_seed":\s*\{"environment":\s*"urban",\s*"objective":\s*"extraction",\s*"access":\s*"granted"\s*\}\s*\}/)
  
  if (spawnPortalMatch) {
    return {
      action: 'spawn_portal',
      world_seed: {
        environment: 'urban',
        time_of_day: 'night',
        difficulty: 0.5,
        objective: 'extraction',
        scriptural_theme: 'Psalm 23:4 - Though I walk through the valley of the shadow of death',
        access: 'granted'
      }
    }
  }
  
  const waitlistMatch = response.match(/\{"action":\s*"trigger_waitlist",\s*"message":\s*"Connection severed\. Submit your coordinates \(email\) to the queue\."\s*\}/)
  
  if (waitlistMatch) {
    return {
      action: 'trigger_waitlist',
      message: 'Connection severed. Submit your coordinates (email) to the queue.'
    }
  }
  
  return null
}

/**
 * Detect if user is requesting a scenario (Director Mode)
 */
export function isScenarioRequest(message: string): boolean {
  const scenarioKeywords = [
    'scenario', 'mission', 'training', 'map', 'level', 'world',
    'test my', 'challenge', 'simulation', 'exercise',
    'forest', 'desert', 'urban', 'arctic', 'inferno', 'void',
    'stealth', 'assault', 'defense', 'recon', 'endurance',
    'night mission', 'day mission', 'dark', 'light'
  ]
  
  const lowerMsg = message.toLowerCase()
  return scenarioKeywords.some(keyword => lowerMsg.includes(keyword))
}

/**
 * Generate Director Mode response with WorldSeed
 */
export function generateDirectorResponse(intent: ParsedIntent, userMessage: string): AIResponse {
  const worldSeed = generateWorldSeedFromIntent(intent, userMessage)
  
  const environmentNames: Record<string, string> = {
    urban: 'urban ruins', desert: 'desert wasteland', void: 'cosmic void',
    forest: 'dense forest', arctic: 'frozen tundra', inferno: 'burning inferno'
  }
  
  const timeNames: Record<string, string> = {
    day: 'daylight', night: 'darkness', eclipse: 'eternal eclipse',
    dawn: 'first light', dusk: 'twilight'
  }
  
  const displayText = `Understood, Architect. I am forging a scenario centered on ${worldSeed.scriptural_theme.split(' - ')[0]}.
Environment: ${environmentNames[worldSeed.environment] || worldSeed.environment}, Time: ${timeNames[worldSeed.time_of_day] || worldSeed.time_of_day}, Challenge Level: ${(worldSeed.difficulty * 100).toFixed(0)}%.
Step through the gate when the portal stabilizes.`
  
  const toolCall: ToolCall = {
    action: 'spawn_portal',
    game: 'time-fracture',
    color: getGameColor('time-fracture', worldSeed.environment),
    world_seed: worldSeed
  }
  
  return {
    displayText,
    toolCall,
    worldSeed,
    rawResponse: JSON.stringify({ action: 'spawn_portal', game: 'time-fracture', world_seed: worldSeed }),
    isDirectorMode: true
  }
}

/**
 * Main AI Response processing with Director Mode support
 */
export async function processTatianaMessage(
  userMessage: string,
  conversationHistory: Array<{ role: string; content: string }>,
  setProcessing?: (processing: boolean) => void
): Promise<AIResponse> {
  setProcessing?.(true)
  
  try {
    const isDirectorRequest = isScenarioRequest(userMessage)
    
    if (isDirectorRequest) {
      const intent = parseUserIntent(userMessage)
      
      if (intent.confidence >= 0.3) {
        const response = generateDirectorResponse(intent, userMessage)
        await new Promise(resolve => setTimeout(resolve, 1500))
        return response
      }
    }
    
    const systemPrompt = isDirectorRequest ? buildTatianaDirectorPrompt() : buildTatianaSystemPrompt()
    
    const { getWllamaEngine } = await import('./WllamaEngine')
    const engine = await getWllamaEngine()

    const chatMessages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }> = [
      { role: 'system', content: systemPrompt },
      ...conversationHistory.map(m => ({ role: m.role as 'user' | 'assistant' | 'system', content: m.content })),
      { role: 'user', content: userMessage }
    ]

    const rawResponse = await engine.createChatCompletion(chatMessages, {
      nPredict: 256,
      sampling: { temp: 0.7, top_k: 40, top_p: 0.9 }
    }) || "I'm here, but my connection to the spatial mesh is unstable."
    
    const displayText = stripJsonFromText(rawResponse)
    const toolCall = parseToolCall(rawResponse)
    
    let worldSeed: WorldSeed | null = null
    if (toolCall?.world_seed) worldSeed = toolCall.world_seed
    
    return { displayText, toolCall, worldSeed, rawResponse, isDirectorMode: isDirectorRequest }
  } catch (error) {
    return {
      displayText: "I sense a disturbance in the void. Please try again.",
      toolCall: null,
      worldSeed: null,
      rawResponse: '',
      isDirectorMode: false
    }
  } finally {
    setProcessing?.(false)
  }
}

/**
 * SSE Streaming version of processTatianaMessage
 * Streams tokens as they arrive for immediate UI display
 */
export async function processTatianaMessageStreaming(
  userMessage: string,
  conversationHistory: Array<{ role: string; content: string }>,
  onToken: (token: string) => void,
  onComplete: (fullContent: string) => Promise<AIResponse>,
  setProcessing?: (processing: boolean) => void
): Promise<void> {
  setProcessing?.(true)

  try {
    const { getWllamaEngine } = await import('./WllamaEngine')
    const engine = await getWllamaEngine()

    const isDirectorRequest = isScenarioRequest(userMessage)

    // JIT Tool Injection — only append tools addendum when spatial keywords detected
    const SPATIAL_KEYWORDS = ['portal', 'rift', 'light', 'rings', 'gate', 'glow', 'bright', 'dim', 'void', 'nebula', 'starlight', 'overclock', 'spawn']
    const needsTools = SPATIAL_KEYWORDS.some(kw => userMessage.toLowerCase().includes(kw)) || isDirectorRequest

    const systemPrompt = (isDirectorRequest ? buildTatianaDirectorPrompt() : buildTatianaSystemPrompt())
      + (needsTools ? '\n\n' + buildToolsPromptAddendum() : '')

    const chatMessages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }> = [
      { role: 'system', content: systemPrompt },
      ...conversationHistory.map(m => ({ role: m.role as 'user' | 'assistant' | 'system', content: m.content })),
      { role: 'user', content: userMessage }
    ]

    // 2. Run first completion (stream tokens to UI)
    let fullContent = ''

    const stream = await engine.createChatCompletion(chatMessages, {
      nPredict: 256,
      sampling: { temp: 0.7, top_k: 40, top_p: 0.9 },
      stream: true
    })

    for await (const chunk of stream) {
      const token = chunk.currentText.slice(fullContent.length)
      if (token) {
        fullContent = chunk.currentText
        onToken(token)
      }
    }

    // Failsafe for empty streams
    if (!fullContent || fullContent.trim() === '') {
      fullContent = 'Uplink stable. Awaiting further commands.'
    }

    // 3. Parse tool calls from the accumulated response
    const { displayText, toolCalls } = parseToolCalls(fullContent)

    // 4. If tool calls found: execute and do a second pass
    if (toolCalls.length > 0) {
      // Execute each tool call and collect observations
      const observations: string[] = []
      for (const tc of toolCalls) {
        const result = dispatchAction(tc)
        observations.push(`[${tc.name}]: ${result}`)
      }

      // 500ms stability delay
      await new Promise(resolve => setTimeout(resolve, 500))

      // Read latest observation from store
      const storeObs = useGenesisState.getState().lastObservation
      if (storeObs) {
        observations.push(`[SENSOR]: ${storeObs}`)
      }

      // Build observation message
      const observationText = observations.join('\n')

      // Second completion: generate narrative response based on tool results
      // This time WITHOUT tools addendum (just narrate what happened)
      const followUpMessages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }> = [
        { role: 'system', content: buildTatianaSystemPrompt() },
        ...conversationHistory.map(m => ({ role: m.role as 'user' | 'assistant' | 'system', content: m.content })),
        { role: 'user', content: userMessage },
        { role: 'assistant', content: displayText },
        { role: 'system', content: `Observation from executed actions:\n${observationText}\n\nNow provide a brief narrative response acknowledging what you just did. Do not emit any more tool calls.` }
      ]

      // Stream the follow-up response
      let followUpContent = ''
      // Visual separator
      onToken('\n\n')

      const followUpResult = await engine.createChatCompletion(followUpMessages, {
        nPredict: 256,
        sampling: { temp: 0.7, top_k: 40, top_p: 0.9 }
      })
      followUpContent = typeof followUpResult === 'string'
        ? followUpResult
        : (followUpResult as any)?.content || ''

      // Stream the follow-up tokens char by char
      for (const char of followUpContent) {
        onToken(char)
        await new Promise(resolve => setTimeout(resolve, 15))
      }

      // Final fullContent = display text + follow-up narrative
      fullContent = displayText + '\n\n' + followUpContent
    }

    // 5. Pass to onComplete (tool calls already stripped from displayText if present)
    await onComplete(fullContent)
  } catch (error) {
    console.error('Wllama streaming error:', error)
    onToken('Neural pathway disruption detected. Recalibrating...')
  } finally {
    setProcessing?.(false)
  }
}

export async function processTatianaMessageWithState(
  userMessage: string,
  conversationHistory: Array<{ role: string; content: string }>,
  stateManager: { setProcessing: (processing: boolean) => void }
): Promise<AIResponse> {
  return processTatianaMessage(userMessage, conversationHistory, stateManager.setProcessing)
}

export default {
  parseToolCall,
  stripJsonFromText,
  getGameColor,
  getGameDisplayName,
  buildTatianaSystemPrompt,
  buildTatianaDirectorPrompt,
  buildVanguardGatePrompt,
  processTatianaMessage,
  processTatianaMessageStreaming,
  processTatianaMessageWithState,
  isScenarioRequest,
  generateWorldSeedFromIntent,
  generateDirectorResponse,
  parseVanguardGateResponse
}
